import React, { useEffect } from "react";

import { Box } from "../../../../../../../mads";
import { useI18n, useLocale } from "../../../../../../../shared/I18n";
import { textDirectionFromLocale } from "../../../../../../../shared/I18n/I18nContext";
import { useViewjobOneGraphModel } from "../../../../../context/ViewJobDataContext";
import { JOB_DETAILS_PROVIDER_ID } from "../../constants/common";
import { JMUIContextProviders } from "../../context/JMUIContextProviders";
import { useFeatureFlags } from "../../context/useFeatureFlags";
import { getJobDetailsModel } from "../../models/JobDetailsComponentModel";
import type { PreferencesCollectionParams } from "../../types/ProviderParams";
import { useSectionErrors } from "../../utils/useSectionErrors";
import { AttributeCollection } from "../common/AttributeCollection";
import { HighlightedAttributes } from "./HighlightedAttributes";
import { SingleLineItems } from "./SingleLineItems";
import getLogReceivedFlag from "./utils/getLogReceivedFlag";
import { usePreferenceSections } from "./utils/usePreferenceSections";

export const PreferencesCollection: React.FunctionComponent<PreferencesCollectionParams> = ({
  salaryInfoModel,
  jobDetailsSection,
  rtl,
  trafficLightSignal,
  knownUserSentimentIdMap,
}) => {
  const gt = useI18n();
  const locale = useLocale();
  const { mobvjtk: mobtk } = useViewjobOneGraphModel() ?? {};
  const { shouldDisablePersonalization } = useFeatureFlags();

  const { errors, onAddError, onClearError } = useSectionErrors();

  const { sections } = usePreferenceSections({
    salaryInfoModel,
    jobDetailsSection,
    onAddError,
    rtl,
    trafficLightSignal,
    knownUserSentimentIdMap,
  });

  useEffect(() => {
    logReceivedFlag(
      jobDetailsSection ? "jobDetailsSectionRendered" : "jobDetailsSectionNotRendered",
    );
  });

  if (!mobtk || !locale) {
    return null;
  }

  const logReceivedFlag = getLogReceivedFlag(mobtk);

  const fieldMap = jobDetailsSection?.contents;
  const showLineItems = fieldMap && Object.keys(fieldMap).length > 0;

  return jobDetailsSection && sections.length > 0 ? (
    <Box id="jobDetailsSection">
      <AttributeCollection
        title={jobDetailsSection.title}
        sections={sections}
        subtitleProps={
          shouldDisablePersonalization
            ? undefined
            : {
                rtl,
                label: gt.gettext("Here’s how the job details align with your [profile|{0}].", [
                  "link",
                ]),
                linkAriaLabel: gt.gettext("profile (opens in a new window)"),
              }
        }
        errors={errors}
        onClearError={onClearError}
        sx={{ marginVertical: 24 }}
      >
        {locale.country === "US" && jobDetailsSection.highlightedAttributesSectionModels?.length ? (
          <HighlightedAttributes
            highlightedAttributesSectionModels={
              jobDetailsSection.highlightedAttributesSectionModels
            }
            tileSectionTitles={sections.map((section) => section.title)}
          />
        ) : null}
        {showLineItems ? <SingleLineItems fieldMap={jobDetailsSection.contents} /> : null}
      </AttributeCollection>
    </Box>
  ) : null;
};

export const PreferencesCollectionWithContext: React.FC = () => {
  return (
    <JMUIContextProviders providerId={JOB_DETAILS_PROVIDER_ID}>
      <PreferencesCollectionRenderer />
    </JMUIContextProviders>
  );
};

export const PreferencesCollectionRenderer: React.FC = () => {
  const vjModel = useViewjobOneGraphModel();
  const locale = useLocale();

  if (!vjModel?.job?.details) {
    return null;
  }

  const jobDetailsSection = vjModel.job.details;
  const isJobExpired = vjModel.job.isExpired;
  const salaryInfoModel = vjModel.compensation.salaryInfoModel;

  const isEnabled = !isJobExpired && (!!jobDetailsSection || !!salaryInfoModel);
  if (!isEnabled) {
    return null;
  }

  const { knownUserSentimentIdMap, trafficLightSignal } = getJobDetailsModel(
    vjModel.jobSeeker.preferences?.jobSeekerSuidsToFactIds,
    vjModel.jobSeeker.matchComparison,
  );

  // Determine RTL from locale
  const rtl = textDirectionFromLocale(locale) === "rtl";

  return (
    <PreferencesCollection
      salaryInfoModel={salaryInfoModel}
      jobDetailsSection={jobDetailsSection}
      rtl={rtl}
      trafficLightSignal={trafficLightSignal}
      knownUserSentimentIdMap={knownUserSentimentIdMap}
    />
  );
};
